import { useWealthOverview } from '@egw/api'
import { Button, ButtonVariant, IconConfirmationCircle, Spinner } from '@egw/components'
import { PIN_INPUT_ID } from '@egw/constants'
import { useTranslation } from '@egw/i18n'
import { InformationCircleIcon } from '@heroicons/react/16/solid'
import { yupResolver } from '@hookform/resolvers/yup'
import cn from 'classnames'
import { useEffect, type FC } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import * as yup from 'yup'
import { sendEvent } from '@egw/events'
import useVGSCollect, { EVGSFormStatus } from '../../hooks/use-vgs-collect'
import styles from './styles.module.css'

interface SetPinProps {
  cardId: string
  onSuccessCb?: () => void
  alias: string
  closeModal: () => void
  isOpen: boolean
}

export const SetPin: FC<SetPinProps> = ({ cardId, onSuccessCb, closeModal, isOpen }) => {
  const { t } = useTranslation('features-account')
  const { refetch: refetchCardDetails } = useWealthOverview({ enabled: false })

  const {
    submitForm: submitVGSForms,
    makeForm,
    getErrorMessage,
    formStatus,
    setFormStatus,
  } = useVGSCollect(PIN_INPUT_ID)

  const onSuccess = async (): Promise<void> => {
    // REFETCH THE CARD DATA ON SETTING OF PIN
    await refetchCardDetails()
    onSuccessCb && onSuccessCb()
  }

  const submitted = formStatus !== EVGSFormStatus.INITIAL && formStatus !== EVGSFormStatus.RETRY

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        setFormStatus(EVGSFormStatus.INITIAL)
        makeForm()
      }, 0)
    }
  }, [isOpen])

  const formMethods = useForm({
    mode: 'onTouched',
    resolver: yupResolver(yup.object().shape({}).required()),
  })

  // -- Layout ---------------------------------------------------------------
  const submitForm = formMethods.handleSubmit(
    () => submitVGSForms({ cardId }, onSuccess),
    () => {},
  )

  return (
    <FormProvider {...formMethods}>
      <form data-testid="setPin_form" onSubmit={submitForm}>
        <div className="flex w-full flex-col justify-end">
          <div
            className={cn(
              `w-full overflow-hidden`,
              {
                // the iframe shouuldnt be destroyed on submitForm, as that would cause the form to fail
                invisible: submitted,
                'h-[70px]': !submitted,
                'h-0': submitted,
                'overflow-hidden': submitted,
              },
              !submitted && styles['iframe-wrapper'],
            )}
          >
            <div id={PIN_INPUT_ID} className="align-center flex justify-center w-full">
              <div className="p-0 w-full mb-4">
                <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
                  <Skeleton count={1} height="50px" width="100%" />
                </SkeletonTheme>
              </div>
            </div>
          </div>
          {formStatus === EVGSFormStatus.INITIAL && (
            <Button
              variant={ButtonVariant.Primary}
              id="setPin"
              type="submit"
              onClick={async () => {
                sendEvent('user_setPIN_click', {
                  eventSource: 'egWealth',
                  category: 'card-pin',
                  subCategory: 'intent',
                })
                await submitForm()
              }}
              className="w-full"
            >
              {t('debit_card.set_pin_title')}
            </Button>
          )}
          {formStatus === EVGSFormStatus.LOADING && (
            <div className="flex flex-col items-center">
              <Spinner className="w-6 h-6 text-gray-100 animate-spin dark:white fill-white" />
              <Button
                variant={ButtonVariant.Primary}
                id="closeModal"
                onClick={closeModal}
                isDisabled
                className="w-full mt-2"
              >
                {t('debit_card.done')}
              </Button>
            </div>
          )}
          {formStatus === EVGSFormStatus.SUCCESS && (
            <>
              <div className="flex justify-center p-8">
                <IconConfirmationCircle />
              </div>
              <Button variant={ButtonVariant.Primary} id="closeModal" onClick={closeModal} className="w-full mt-2">
                {t('debit_card.done')}
              </Button>
            </>
          )}
          {formStatus === EVGSFormStatus.RETRY && (
            <>
              <div className="flex bg-alertErrorBg p-3 rounded-md my-2">
                <InformationCircleIcon className="w-6 h-6 fill-red-500" />
                <p className="text-sm text-alertErrorText ml-2">{getErrorMessage() || t('debit_card.problem')} </p>
              </div>{' '}
              <Button
                variant={ButtonVariant.Primary}
                id="setPinRetry"
                onClick={async () => {
                  await submitForm()
                }}
                className="w-full"
              >
                {t('debit_card.set_pin_title')}
              </Button>
            </>
          )}
          {formStatus === EVGSFormStatus.FAILURE && (
            <>
              <div className="flex bg-alertErrorBg p-3 rounded-md my-2">
                <InformationCircleIcon className="w-6 h-6 fill-red-500" />
                <p className="text-sm text-alertErrorText ml-2">{getErrorMessage() || t('debit_card.problem')} </p>
              </div>
              <Button variant={ButtonVariant.Primary} id="closeModal" onClick={closeModal} className="w-full">
                {t('debit_card.back')}
              </Button>
            </>
          )}
        </div>
      </form>
    </FormProvider>
  )
}
